import React, { useState } from "react"
import PropTypes from "prop-types"
import { Button } from "react-bootstrap"

import { listUtils } from "../../../shared_components/widgets/utils"
import { LanzaTable, UniversalFilter } from "../../../shared_components/widgets/LanzaTable"
import { LocaleFormattedUser, LocaleFormattedDate } from "../../internationalization/LocaleFormatted"
import { withSampleConfig } from "../../../config/SampleConfigProvider"
import { showModal, hideModal, isNumber } from "../../../shared_components/utils"
import ModalViewSampleRound from "./ModalViewSampleRound"
import ModalCreateSampleRound from "./ModalCreateSampleRound"
import SampleRoundTemplates from "./SampleRoundTemplates"
import { getFormattedMessage } from "../../shared_components_registration"
import { useSensePageWidth } from "../SampleScheduleTab/FormSamplePoint"

const SampleRoundsTab = ({
  getSamplingPointMethods,
  getReactors,
  reloadSampleRoundsTable,
  reloadSampleTable,
  querySampleRounds,
  sampleRoundsTableRef,
  isNSView = false,
  sample_point_data = [],
  sample_template_data = [],
  user_permissions = [],
}) => {
  const pageWidth = useSensePageWidth()

  const [selectedSampleRound, setSelectedSampleRound] = useState(null)
  const [viewSampleRoundModal, setViewSampleRoundModal] = useState(false)
  const [newSampleRoundModal, setNewSampleRoundModal] = useState(false)
  const [sampleRoundModalReadOnly, setSampleRoundModalReadOnly] = useState(false)

  const viewSampleRound = (event, sample_round, read_only) => {
    event.preventDefault()
    sample_round.samples.forEach((smp) => getSamplingPointMethods([smp.sampling_point], true))
    setSelectedSampleRound(sample_round)
    setSampleRoundModalReadOnly(read_only)
    showModal(setViewSampleRoundModal)
  }

  const TableColumnFiter = ({ filterProps = {}, customOptions }) => {
    const { column, preFilteredRows } = filterProps
    return (
      <UniversalFilter
        column={column}
        setFilter={column.setFilter}
        preFilteredRows={preFilteredRows}
        filterType={"select"}
        customOptions={customOptions}
        isClearable={false}
      />
    )
  }

  return (
    <>
      <SampleRoundTemplates />
      <div className={"row"}>
        <div className={"col-4"}>
          <div style={{ margin: "5px 0px" }}>
            <>
              <span className={"fa fa-retweet"} />
              &nbsp;&nbsp;
              {getFormattedMessage(
                `samplemanagement.tab.${isNSView ? "ns" : "sample"}.rounds`,
                `${isNSView ? "NS" : "Sample"} Rounds`,
              )}
            </>
          </div>
        </div>
        <div className={"col-8"} align={"right"}>
          <Button
            variant={"outline-primary"}
            size={"sm"}
            onClick={(event) => {
              event.preventDefault()
              reloadSampleRoundsTable()
            }}
            style={{ marginRight: "5px" }}
          >
            <span className={"fa fa-refresh"} />
            &nbsp;
            {getFormattedMessage("general.refresh", "Refresh")}
          </Button>
          <Button
            variant={"primary"}
            size={"sm"}
            disabled={!user_permissions.includes("add_sampleround")}
            onClick={(e) => {
              e.preventDefault()
              showModal(setNewSampleRoundModal)
            }}
          >
            <span className={"fa fa-play text_white"} />
            &nbsp;
            {getFormattedMessage("general.start", "Start")}
          </Button>
        </div>
      </div>
      <hr style={{ margin: "10px 0px 25px 0px" }} />

      <LanzaTable
        name={"Sample Round Table"}
        ref={sampleRoundsTableRef} // Server Pagination Related
        getDataPage={querySampleRounds} // Server Pagination Related
        className={"table table-bordered table-hover tbl_lanzatable table-sm"}
        columns={[
          {
            Header: getFormattedMessage("general.id", "ID"),
            accessor: "id",
            style: { width: "65px" },
          },
          {
            Header: getFormattedMessage("general.description", "Description"),
            accessor: "description",
            Cell: ({ value }) => value || "-",
            largeOnly: true,
          },
          {
            Header: getFormattedMessage("general.template", "Template"),
            accessor: "sample_round_template.name",
            Cell: ({ value }) => value || "-",
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={(sample_template_data || []).map((t) => {
                  return {
                    value: t.name,
                    text: t.name,
                  }
                })}
              />
            ),
            filter: "equals",
          },
          {
            Header: getFormattedMessage("samplemanagement.start.time", "Start Time"),
            accessor: "start_time",
            disableFilters: true,
            Cell: ({ value }) => <LocaleFormattedDate date={value} />,
            className: "text-center",
          },
          {
            Header: getFormattedMessage("samplemanagement.end.time", "End Time"),
            accessor: "end_time",
            disableFilters: true,
            Cell: ({ value }) => (value ? <LocaleFormattedDate date={value} /> : "-"),
            className: "text-center",
          },
          {
            Header: getFormattedMessage("general.duration", "Duration"),
            accessor: "duration_min",
            disableFilters: true,
            Cell: ({ value }) => (isNumber(value) ? value.toFixed(2) : "-"),
            style: { width: "100px" },
            className: "text-center",
            largeOnly: true,
          },
          {
            Header: getFormattedMessage("general.reactors", "Reactors"),
            id: "reactors",
            accessor: "samples",
            disableSortBy: true,
            Cell: ({ value }) => getReactors(value, "V2").join(", "),
            Filter: (filterProps) => {
              return (
                <TableColumnFiter
                  filterProps={filterProps}
                  customOptions={getReactors(sample_point_data, "V1").map((r) => {
                    return {
                      value: r,
                      text: r,
                    }
                  })}
                />
              )
            },
            filter: "equals",
          },
          {
            Header: getFormattedMessage("samplemanagement.sampling.points", "Sampling Points"),
            id: "sampling_points",
            accessor: "samples",
            disableSortBy: true,
            Cell: ({ value }) => {
              return listUtils
                .removeDuplicates(
                  value.map((smpl) => {
                    return smpl.sampling_point["sample_conn_no"]
                  }),
                )
                .join(", ")
            },
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={sample_point_data.map((sp) => {
                  return {
                    value: sp["sample_conn_no"],
                    text: sp["sample_conn_no"],
                  }
                })}
              />
            ),
            filter: "equals",
          },
          {
            Header: getFormattedMessage("fermmanagement.started.by", "Started By"),
            accessor: "created_by",
            Cell: ({ value }) => <LocaleFormattedUser user={value} />,
            largeOnly: true,
          },
          {
            Header: getFormattedMessage("general.status", "Status"),
            id: "status",
            accessor: "is_active",
            Cell: ({ value }) => {
              if (value) {
                return (
                  <span className={"text-success"}>
                    <span className={"fa fa-play text-success"} />
                    &nbsp;
                    {getFormattedMessage("general.running", "Running")}
                  </span>
                )
              } else {
                return <>{getFormattedMessage("general.complete", "Complete")}</>
              }
            },
            Filter: (filterProps) => (
              <TableColumnFiter
                filterProps={filterProps}
                customOptions={[
                  { value: true, text: "Running", id: "general.running" },
                  { value: false, text: "Complete", id: "general.complete" },
                ]}
              />
            ),
            className: "text-center",
          },
          {
            Header: "",
            id: "actions",
            accessor: (row) => {
              return (
                <>
                  {row["is_active"] && (
                    <div>
                      <Button
                        variant={"outline-primary"}
                        size={"sm"}
                        style={{ fontSize: "90%" }}
                        disabled={!user_permissions.includes("change_sampleround")}
                        onClick={(event) => viewSampleRound(event, row, false)}
                      >
                        <span className={"fa fa-stop text-danger"} />
                        &nbsp;
                        {getFormattedMessage("samplemanagement.complete.round", "Complete Round")}
                      </Button>
                    </div>
                  )}
                  <div>
                    <Button
                      variant={"outline-primary"}
                      size={"sm"}
                      style={{ marginTop: "2px", fontSize: "90%" }}
                      onClick={(event) => viewSampleRound(event, row, true)}
                    >
                      <span className={"fa fa-window-restore"} />
                      &nbsp;
                      {getFormattedMessage("general.view", "View")}
                    </Button>
                  </div>
                  <div>
                    <a
                      className={"btn btn-sm link_btn_outline_primary"}
                      href={
                        "/laboratory/sample-management/sample-round/" + row.id + "/download_sample_schedule/"
                      }
                      style={{ padding: "5px", marginTop: "2px", fontSize: "90%" }}
                      target={"_blank"}
                    >
                      <span className={"fa fa-download"} />
                      &nbsp;&nbsp;
                      {getFormattedMessage("general.download", "Download")}
                    </a>
                  </div>
                </>
              )
            },
            className: "text-center",
            disableSortBy: true,
            disableFilters: true,
          },
        ]
          .filter((col) => !col?.largeOnly || pageWidth > 900)
          .map((col) => {
            return {
              ...col,
              style: { fontSize: "90%", ...(col["style"] || {}) },
            }
          })}
        allowGlobalFilter={true}
        allowSorting={true}
        allowFilters={true}
        initialTableOptions={{
          sortBy: [{ id: "id", desc: true }],
          pageSize: 10,
        }}
      />

      {/*----- Modals -----*/}
      {selectedSampleRound && viewSampleRoundModal && (
        <ModalViewSampleRound
          sampleround={selectedSampleRound}
          show={viewSampleRoundModal}
          onHide={() => {
            setSelectedSampleRound(null)
            hideModal(setViewSampleRoundModal)
          }}
          onSuccess={() => {
            setSelectedSampleRound(null)
            hideModal(setViewSampleRoundModal)
            reloadSampleRoundsTable()
            reloadSampleTable()
          }}
          readonly={sampleRoundModalReadOnly}
        />
      )}

      {newSampleRoundModal && user_permissions.includes("add_sampleround") && (
        <ModalCreateSampleRound
          show={newSampleRoundModal}
          onHide={() => hideModal(setNewSampleRoundModal)}
          onSuccess={() => {
            hideModal(setNewSampleRoundModal)
            reloadSampleRoundsTable()
            reloadSampleTable()
          }}
          sampleroundTemplate={null}
        />
      )}
      {/*----- /Modals -----*/}
    </>
  )
}

export default withSampleConfig(SampleRoundsTab)

SampleRoundsTab.propTypes = {
  getSamplingPointMethods: PropTypes.func,
  getReactors: PropTypes.func,
  reloadSampleRoundsTable: PropTypes.func,
  reloadSampleTable: PropTypes.func,
  querySampleRounds: PropTypes.func,
  sampleRoundsTableRef: PropTypes.object,
  isNSView: PropTypes.bool,
  sample_point_data: PropTypes.array,
  sample_template_data: PropTypes.array,
  user_permissions: PropTypes.array,
}
